import { useState, useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { useToast } from '@/hooks/use-toast';
import { Camera, User } from 'lucide-react';
import { useIOSDetection } from '@/hooks/use-ios-detection';
import { BookAvatarSelector } from './BookAvatarSelector';
import { clearImageCache } from '@/lib/imageCache';
import { notifyAvatarChange } from '@/lib/profileSnapshot';
import { preloadAvatar } from '@/lib/avatarPreloader';

interface AvatarUploadProps {
  userId: string;
  url: string | null;
  onUploadComplete: (url: string) => void;
  size?: 'sm' | 'md' | 'lg';
  hideButton?: boolean;
  profileId?: string; // Add profileId to notify the system
}

export function AvatarUpload({ url, onUploadComplete, size = 'md', hideButton = false, profileId }: AvatarUploadProps) {
  const { toast } = useToast();
  const isIOS = useIOSDetection();
  const [avatarUrl, setAvatarUrl] = useState(url);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Sync local state with prop changes (fixes old account avatar updates)
  useEffect(() => {
    setAvatarUrl(url);
    setRefreshKey(prev => prev + 1);
  }, [url]);

  const sizeClasses = {
    sm: 'h-12 w-12',
    md: 'h-16 w-16',
    lg: 'h-24 w-24',
  };

  const handleAvatarSelect = async (selectedUrl: string) => {
    // Clear old avatar from cache for instant update
    if (avatarUrl) {
      clearImageCache(avatarUrl);
    }
    
    // Preload the new avatar immediately
    await preloadAvatar(selectedUrl);
    
    setAvatarUrl(selectedUrl);
    setRefreshKey(prev => prev + 1);
    onUploadComplete(selectedUrl);
    
    // Notify the system that avatar changed (triggers navbar refresh animation)
    if (profileId) {
      notifyAvatarChange(profileId, selectedUrl);
    }

    toast({
      title: 'Avatar updated',
      description: 'Your profile picture has been updated.',
    });
  };

  return (
    <>
      <div className="relative">
        <Avatar 
          className={`${sizeClasses[size]} cursor-pointer border-2 border-primary/30 hover:opacity-90 transition-opacity`} 
          onClick={() => setIsDialogOpen(true)}
        >
          <AvatarImage 
            src={(() => {
              if (!avatarUrl) return undefined;
              
              // Add iOS cache busting
              if (isIOS) {
                try {
                  const url = new URL(avatarUrl);
                  url.searchParams.set('refresh', refreshKey.toString());
                  return url.toString();
                } catch {
                  const separator = avatarUrl.includes('?') ? '&' : '?';
                  return `${avatarUrl}${separator}refresh=${refreshKey}`;
                }
              }
              
              return avatarUrl;
            })()} 
          />
          <AvatarFallback>
            <User className="h-1/2 w-1/2" />
          </AvatarFallback>
        </Avatar>
        {!hideButton && (
          <Button 
            size="sm" 
            variant="secondary" 
            className="absolute -bottom-1 -right-1 rounded-full h-7 w-7 p-0" 
            onClick={() => setIsDialogOpen(true)}
          >
            <Camera className="h-3 w-3" />
          </Button>
        )}
      </div>

      <BookAvatarSelector
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSelect={handleAvatarSelect}
        currentAvatar={avatarUrl}
      />
    </>
  );
}
